import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  RiQrCodeLine,
  RiShareForwardLine,
  RiCameraLine,
  RiEditLine,
  RiSuitcaseLine,
  RiFileTextLine,
  RiVideoLine,
  RiCheckLine,
  RiCloseLine,
} from "react-icons/ri";
import { LuCopy, LuChevronDown, LuChevronUp } from "react-icons/lu";
import { setProfile } from "../../redux/slice/playerProfileSlice";
import {
  fetchUserProfile,
  updatePlayerRoleDetails,
} from "./api/userProfileApi";
import type { UpdatePlayerRoleDetailsRequest } from "./api/userProfileApi";
import type { IPlayerProfileResponse } from "../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/playerProfile";
import "./UserProfilePage.scss";
import "./tabs/profileTabs.scss";
import ProfileAnalysisTab from "./tabs/ProfileAnalysisTab";
import ProfileMatchesTab from "./tabs/ProfileMatchesTab";
import ProfileTeamsTab from "./tabs/ProfileTeamsTab";
import defaultAvatar from "../../assets/images/default_avatar.png";
import { useAppDispatch } from "../../redux/hooks";

const mainTabs = [
  "Personal Info",
  "Roles & Capabilities" /*, "Billing"*/,
] as const;
const subTabs = [
  // "Spotlight",
  // "Stats",
  // "Media",
  "Profile",
  "Analysis",
  "Matches",
  "Teams",
] as const;

const cdnBaseUrl = (import.meta.env.VITE_IMAGE_CDN_BASE_URL ?? "").replace(
  /\/$/,
  "",
);

const playerRoleLabels: Record<string, string> = {
  BAT_ALL_ROUNDER: "Bat All Rounder",
  BOWL_ALL_ROUNDER: "Bowl All Rounder",
  BATSMAN: "Batsman",
  BOWLER: "Bowler",
  WICKET_KEEPER: "Wicket Keeper",
  CAPTAIN: "Captain",
};

const battingStyleLabels: Record<string, string> = {
  RIGHT_HANDED: "Right Handed",
  LEFT_HANDED: "Left Handed",
};

const bowlingStyleLabels: Record<string, string> = {
  RIGHT_ARM_FAST: "Right Arm Fast",
  RIGHT_ARM_MEDIUM: "Right Arm Medium",
  RIGHT_ARM_SPIN: "Right Arm Spin",
  LEFT_ARM_FAST: "Left Arm Fast",
  LEFT_ARM_MEDIUM: "Left Arm Medium",
  LEFT_ARM_SPIN: "Left Arm Spin",
  SLOW_LEFT_ARM_ORTHODOX: "Slow Left Arm Orthodox",
  LEG_BREAK: "Leg Break",
  OFF_BREAK: "Off Break",
};

const formatEnum = (
  value: string | undefined,
  labels: Record<string, string>,
): string => {
  if (!value) return "-";
  return (
    labels[value] ??
    value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

const getAvatarUrl = (imageUri: string | undefined): string => {
  if (!imageUri) return "";
  if (imageUri.startsWith("http")) return imageUri;
  return cdnBaseUrl ? `${cdnBaseUrl}/${imageUri.replace(/^\//, "")}` : imageUri;
};

type UserProfilePageProps = {
  /** Render only the Roles & Capabilities editor (used inside the new profile page for the signed-in user). */
  rolesOnly?: boolean;
  /** Called after roles were saved successfully, so the host page can refresh its own copy of the profile. */
  onRolesSaved?: () => void;
};

const UserProfilePage: React.FC<UserProfilePageProps> = ({ rolesOnly = false, onRolesSaved }) => {
  const [activeMainTab, setActiveMainTab] = useState<string>(rolesOnly ? "Roles & Capabilities" : mainTabs[0]);
  const [activeSubTab, setActiveSubTab] = useState<string>(subTabs[0]);
  const [profileData, setProfileData] = useState<IPlayerProfileResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [isEditingRoles, setIsEditingRoles] = useState(false);
  const [isSavingRoles, setIsSavingRoles] = useState(false);
  const dispatch = useAppDispatch();
  const [roleToggles, setRoleToggles] = useState({
    player: true,
    umpire: false,
    scorer: false,
    streamer: false,
  });
  const [umpireFields, setUmpireFields] = useState({
    locations: [] as string[],
    locationInput: "",
    experience: "",
    fees: "",
    frequency: "",
  });
  const [scorerFields, setScorerFields] = useState({
    experienceYears: "",
    scoringInterfacePreferred: [] as string[],
    city: "",
    familiarScoringMethods: [] as string[],
    certificateUrl: "",
    feesPerMatch: "",
    feesPerDay: "",
    languagesKnown: [] as string[],
    bio: "",
  });
  const [streamerFields, setStreamerFields] = useState({
    cameraSetup: [] as string[],
    streamingDevice: [] as string[],
    audioSetup: [] as string[],
    streamingPlatforms: [] as string[],
    pricingPerMatch: "",
    pricingPerDay: "",
    availableForPaidStreaming: false,
    sampleStreamLink: "",
  });
  const [openDropdown, setOpenDropdown] = useState<
    | "cameraSetup"
    | "streamingDevice"
    | "audioSetup"
    | "streamingPlatforms"
    | "scoringInterfacePreferred"
    | "familiarScoringMethods"
    | "languagesKnown"
    | null
  >(null);
  const [tempSelections, setTempSelections] = useState<{
    cameraSetup: string[];
    streamingDevice: string[];
    audioSetup: string[];
    streamingPlatforms: string[];
    scoringInterfacePreferred: string[];
    familiarScoringMethods: string[];
    languagesKnown: string[];
  }>({
    cameraSetup: [],
    streamingDevice: [],
    audioSetup: [],
    streamingPlatforms: [],
    scoringInterfacePreferred: [],
    familiarScoringMethods: [],
    languagesKnown: [],
  });

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const loadProfile = async () => {
      try {
        const data = await fetchUserProfile();
        setProfileData(data);
      } catch (err) {
        console.error("Failed to load profile", err);
        toast.error("Failed to load profile. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  // Sync toggles and fields when profile data loads
  useEffect(() => {
    if (profileData) {
      const { playerResponse, scorerProfile, streamerProfile } = profileData;

      setRoleToggles({
        player: true,
        umpire: playerResponse.isUmpire ?? false,
        scorer: scorerProfile != null && scorerProfile.active,
        streamer: streamerProfile != null && streamerProfile.active,
      });

      setUmpireFields({
        locations: (playerResponse.locations ?? []).filter(Boolean),
        locationInput: "",
        experience:
          playerResponse.experience != null
            ? String(playerResponse.experience)
            : "",
        fees: playerResponse.fees != null ? String(playerResponse.fees) : "",
        frequency: playerResponse.frequency ?? "",
      });

      if (scorerProfile) {
        setScorerFields({
          experienceYears: String(scorerProfile.experienceYears ?? ""),
          scoringInterfacePreferred:
            scorerProfile.scoringInterfacePreferred ?? [],
          city: scorerProfile.city ?? "",
          familiarScoringMethods: scorerProfile.familiarScoringMethods ?? [],
          certificateUrl: scorerProfile.certificateUrl ?? "",
          feesPerMatch: String(scorerProfile.feesPerMatch ?? ""),
          feesPerDay: String(scorerProfile.feesPerDay ?? ""),
          languagesKnown: scorerProfile.languagesKnown ?? [],
          bio: scorerProfile.bio ?? "",
        });
      }

      if (streamerProfile) {
        setStreamerFields({
          cameraSetup: Array.isArray(streamerProfile.cameraSetup)
            ? streamerProfile.cameraSetup
            : typeof streamerProfile.cameraSetup === "string"
              ? [streamerProfile.cameraSetup]
              : [],
          streamingDevice: Array.isArray(streamerProfile.streamingDevice)
            ? streamerProfile.streamingDevice
            : typeof streamerProfile.streamingDevice === "string"
              ? [streamerProfile.streamingDevice]
              : [],
          audioSetup: Array.isArray(streamerProfile.audioSetup)
            ? streamerProfile.audioSetup
            : typeof streamerProfile.audioSetup === "string"
              ? [streamerProfile.audioSetup]
              : [],
          streamingPlatforms: Array.isArray(streamerProfile.streamingPlatforms)
            ? streamerProfile.streamingPlatforms
            : typeof streamerProfile.streamingPlatforms === "string"
              ? [streamerProfile.streamingPlatforms]
              : [],
          pricingPerMatch: String(streamerProfile.pricingPerMatch ?? ""),
          pricingPerDay: String(streamerProfile.pricingPerDay ?? ""),
          availableForPaidStreaming:
            streamerProfile.availableForPaidStreaming ?? false,
          sampleStreamLink: streamerProfile.sampleStreamLink ?? "",
        });
      }
    }
  }, [profileData]);

  const player = profileData?.playerResponse;

  const playerName = [
    player?.playerFirstName,
    player?.playerMiddleName,
    player?.playerLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const description = [
    player?.battingStyle
      ? `${formatEnum(player.battingStyle, battingStyleLabels)} batter`
      : "",
    player?.bowlingStyle
      ? `${formatEnum(player.bowlingStyle, bowlingStyleLabels)} bowler`
      : "",
  ]
    .filter(Boolean)
    .join(", ");

  const profileDetails = [
    {
      label: "Player Role",
      value: formatEnum(player?.playerRole, playerRoleLabels),
    },
    {
      label: "Batting Style",
      value: formatEnum(player?.battingStyle, battingStyleLabels),
    },
    {
      label: "Bowling Style",
      value: formatEnum(player?.bowlingStyle, bowlingStyleLabels),
    },
    { label: "Gender", value: player?.gender || "-" },
  ];

  const roles = [
    {
      key: "umpire" as const,
      label: "Umpire",
      icon: <RiSuitcaseLine />,
      enabled: roleToggles.umpire,
      alwaysOn: false,
    },
    {
      key: "scorer" as const,
      label: "Scorer",
      icon: <RiFileTextLine />,
      enabled: roleToggles.scorer,
      alwaysOn: false,
    },
    {
      key: "streamer" as const,
      label: "Streamer",
      icon: <RiVideoLine />,
      enabled: roleToggles.streamer,
      alwaysOn: false,
    },
  ];

  const handleToggleRole = useCallback(
    (key: keyof typeof roleToggles) => {
      if (!isEditingRoles) return;
      setRoleToggles((prev) => ({ ...prev, [key]: !prev[key] }));
    },
    [isEditingRoles],
  );

  const handleEditRoles = useCallback(() => {
    setIsEditingRoles(true);
  }, []);

  const handleSaveRoles = useCallback(async () => {
    const originalScorer = profileData?.scorerProfile;
    const originalPlayer = profileData?.playerResponse;
    const originalStreamer = profileData?.streamerProfile;

    const payload: UpdatePlayerRoleDetailsRequest = {
      scorerDetails: roleToggles.scorer
        ? {
            experienceYears: Number(scorerFields.experienceYears) || 0,
            city: scorerFields.city,
            familiarScoringMethods: scorerFields.familiarScoringMethods,
            matchFormatsWorkedOn: [],
            scoringInterfacePreferred: scorerFields.scoringInterfacePreferred,
            feesPerMatch: Number(scorerFields.feesPerMatch) || 0,
            feesPerDay: Number(scorerFields.feesPerDay) || 0,
            languagesKnown: scorerFields.languagesKnown,
            bio: scorerFields.bio,
            active: true,
          }
        : originalScorer
          ? {
              experienceYears: originalScorer.experienceYears ?? 0,
              city: originalScorer.city ?? "",
              familiarScoringMethods:
                originalScorer.familiarScoringMethods ?? [],
              matchFormatsWorkedOn: [],
              scoringInterfacePreferred:
                originalScorer.scoringInterfacePreferred ?? [],
              feesPerMatch: originalScorer.feesPerMatch ?? 0,
              feesPerDay: originalScorer.feesPerDay ?? 0,
              languagesKnown: originalScorer.languagesKnown ?? [],
              bio: originalScorer.bio ?? "",
              active: false,
            }
          : undefined,
      umpireDetails: roleToggles.umpire
        ? {
            location: umpireFields.locations,
            experience: Number(umpireFields.experience) || 0,
            fees: Number(umpireFields.fees) || 0,
            frequency: umpireFields.frequency,
            active: true,
          }
        : originalPlayer?.isUmpire
          ? {
              location: (originalPlayer.locations ?? []).filter(Boolean),
              experience: originalPlayer.experience ?? 0,
              fees: originalPlayer.fees ?? 0,
              frequency: originalPlayer.frequency ?? "",
              active: false,
            }
          : undefined,
      streamerDetails: roleToggles.streamer
        ? {
            cameraSetup: streamerFields.cameraSetup,
            streamingDevice: streamerFields.streamingDevice,
            audioSetup: streamerFields.audioSetup,
            streamingPlatforms: streamerFields.streamingPlatforms,
            pricingPerMatch: Number(streamerFields.pricingPerMatch) || 0,
            pricingPerDay: Number(streamerFields.pricingPerDay) || 0,
            availableForPaidStreaming: streamerFields.availableForPaidStreaming,
            sampleStreamLink: streamerFields.sampleStreamLink,
            active: true,
          }
        : originalStreamer
          ? {
              cameraSetup: Array.isArray(originalStreamer.cameraSetup)
                ? originalStreamer.cameraSetup
                : [],
              streamingDevice: Array.isArray(originalStreamer.streamingDevice)
                ? originalStreamer.streamingDevice
                : [],
              audioSetup: Array.isArray(originalStreamer.audioSetup)
                ? originalStreamer.audioSetup
                : [],
              streamingPlatforms: Array.isArray(
                originalStreamer.streamingPlatforms,
              )
                ? originalStreamer.streamingPlatforms
                : [],
              pricingPerMatch: originalStreamer.pricingPerMatch ?? 0,
              pricingPerDay: originalStreamer.pricingPerDay ?? 0,
              availableForPaidStreaming:
                originalStreamer.availableForPaidStreaming ?? false,
              sampleStreamLink: originalStreamer.sampleStreamLink ?? "",
              active: false,
            }
          : undefined,
    };

    setIsSavingRoles(true);
    try {
      await updatePlayerRoleDetails(payload);
      setIsEditingRoles(false);
      setExpandedRole(null);
      toast.success("Roles updated successfully!");
      const updatedProfile = await fetchUserProfile();
      dispatch(setProfile(updatedProfile));
      onRolesSaved?.();
    } catch (err) {
      console.error("Failed to update roles", err);
      toast.error("Failed to update roles. Please try again.");
    } finally {
      setIsSavingRoles(false);
    }
  }, [profileData, roleToggles, scorerFields, umpireFields, streamerFields, onRolesSaved]);

  const handleCancelEditRoles = useCallback(() => {
    if (profileData) {
      const { playerResponse, scorerProfile, streamerProfile } = profileData;

      setRoleToggles({
        player: true,
        umpire: playerResponse.isUmpire ?? false,
        scorer: scorerProfile != null && scorerProfile.active,
        streamer: streamerProfile != null && streamerProfile.active,
      });

      setUmpireFields({
        locations: (playerResponse.locations ?? []).filter(Boolean),
        locationInput: "",
        experience:
          playerResponse.experience != null
            ? String(playerResponse.experience)
            : "",
        fees: playerResponse.fees != null ? String(playerResponse.fees) : "",
        frequency: playerResponse.frequency ?? "",
      });

      if (scorerProfile) {
        setScorerFields({
          experienceYears: String(scorerProfile.experienceYears ?? ""),
          scoringInterfacePreferred:
            scorerProfile.scoringInterfacePreferred ?? [],
          city: scorerProfile.city ?? "",
          familiarScoringMethods: scorerProfile.familiarScoringMethods ?? [],
          certificateUrl: scorerProfile.certificateUrl ?? "",
          feesPerMatch: String(scorerProfile.feesPerMatch ?? ""),
          feesPerDay: String(scorerProfile.feesPerDay ?? ""),
          languagesKnown: scorerProfile.languagesKnown ?? [],
          bio: scorerProfile.bio ?? "",
        });
      } else {
        setScorerFields({
          experienceYears: "",
          scoringInterfacePreferred: [],
          city: "",
          familiarScoringMethods: [],
          certificateUrl: "",
          feesPerMatch: "",
          feesPerDay: "",
          languagesKnown: [],
          bio: "",
        });
      }

      if (streamerProfile) {
        setStreamerFields({
          cameraSetup: Array.isArray(streamerProfile.cameraSetup)
            ? streamerProfile.cameraSetup
            : typeof streamerProfile.cameraSetup === "string"
              ? [streamerProfile.cameraSetup]
              : [],
          streamingDevice: Array.isArray(streamerProfile.streamingDevice)
            ? streamerProfile.streamingDevice
            : typeof streamerProfile.streamingDevice === "string"
              ? [streamerProfile.streamingDevice]
              : [],
          audioSetup: Array.isArray(streamerProfile.audioSetup)
            ? streamerProfile.audioSetup
            : typeof streamerProfile.audioSetup === "string"
              ? [streamerProfile.audioSetup]
              : [],
          streamingPlatforms: Array.isArray(streamerProfile.streamingPlatforms)
            ? streamerProfile.streamingPlatforms
            : typeof streamerProfile.streamingPlatforms === "string"
              ? [streamerProfile.streamingPlatforms]
              : [],
          pricingPerMatch: String(streamerProfile.pricingPerMatch ?? ""),
          pricingPerDay: String(streamerProfile.pricingPerDay ?? ""),
          availableForPaidStreaming:
            streamerProfile.availableForPaidStreaming ?? false,
          sampleStreamLink: streamerProfile.sampleStreamLink ?? "",
        });
      } else {
        setStreamerFields({
          cameraSetup: [],
          streamingDevice: [],
          audioSetup: [],
          streamingPlatforms: [],
          pricingPerMatch: "",
          pricingPerDay: "",
          availableForPaidStreaming: false,
          sampleStreamLink: "",
        });
      }
    }
    setIsEditingRoles(false);
    setExpandedRole(null);
  }, [profileData]);

  const handleCopyCode = () => {
    if (player?.playerCode) {
      navigator.clipboard.writeText(player.playerCode);
      toast.success("Player code copied!");
    }
  };

  if (loading) {
    return (
      <div className={`user-profile-page-container${rolesOnly ? " embedded" : ""}`}>
        <div className="user-profile-page-wrapper">
          <div className="profile-skeleton">
            <div className="skeleton-badge shimmer" />
            <div className="skeleton-hero">
              <div className="skeleton-avatar shimmer" />
              <div className="skeleton-info">
                <div className="skeleton-line skeleton-name shimmer" />
                <div className="skeleton-line skeleton-code shimmer" />
                <div className="skeleton-line skeleton-desc shimmer" />
              </div>
              <div className="skeleton-actions">
                <div className="skeleton-btn shimmer" />
                <div className="skeleton-btn shimmer" />
              </div>
            </div>
            <div className="skeleton-tabs">
              <div className="skeleton-tab-row">
                <div className="skeleton-line skeleton-tab shimmer" />
                <div className="skeleton-line skeleton-tab shimmer" />
                <div className="skeleton-line skeleton-tab shimmer" />
              </div>
              <div className="skeleton-divider" />
              <div className="skeleton-chip-row">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div className="skeleton-chip shimmer" key={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profileData || !player) {
    return (
      <div className={`user-profile-page-container${rolesOnly ? " embedded" : ""}`}>
        <div className="user-profile-page-wrapper">
          <div className="profile-loading">Unable to load profile data.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`user-profile-page-container${rolesOnly ? " embedded" : ""}`}>
      <div className="user-profile-page-wrapper">
        {!rolesOnly && (
          <>
        {/* Hero Card */}
        <div className="profile-hero-card">
          <div className="profile-hero-content">
            <div className="profile-avatar-section">
              <div className="profile-avatar">
                <img
                  src={getAvatarUrl(player.imageUri) || defaultAvatar}
                  alt={playerName}
                />
                <button className="avatar-camera-btn" type="button">
                  <RiCameraLine />
                </button>
              </div>
            </div>

            <div className="profile-info-section">
              <h1 className="player-name">{playerName}</h1>
              {player.playerCode && (
                <div className="player-code">
                  <span>Player Code : </span>
                  <span className="code-value">{player.playerCode}</span>
                  <button
                    className="copy-btn"
                    type="button"
                    onClick={handleCopyCode}
                  >
                    <LuCopy />
                  </button>
                </div>
              )}
              <p className="player-description">{description}</p>
            </div>

            <div className="profile-actions-section">
              <button className="action-btn" type="button">
                <RiQrCodeLine />
                <span>My QR</span>
              </button>
              {/* <button className="action-btn" type="button">
                <RiShareForwardLine />
                <span>Share Profile</span>
              </button> */}
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="profile-tabs-section">
          <div className="main-tabs">
            {mainTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                className={`main-tab ${activeMainTab === tab ? "active" : ""}`}
                onClick={() => setActiveMainTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeMainTab === "Personal Info" && (
            <>
              <hr className="tab-divider" />
              <div className="sub-tabs">
                {subTabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`sub-tab ${activeSubTab === tab ? "active" : ""}`}
                    onClick={() => setActiveSubTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

          </>
        )}

        {/* Tab Content Area */}
        <div className="profile-tab-content">
          {activeMainTab === "Personal Info" && activeSubTab === "Profile" && (
            <div className="player-profile-card">
              <div className="player-profile-header">
                <h2>Player Profile</h2>
                {/* <button className="edit-btn" type="button">
                  <RiEditLine />
                </button> */}
              </div>
              <div className="player-profile-details">
                {profileDetails.map((item) => (
                  <div className="detail-row" key={item.label}>
                    <span className="detail-label">{item.label}</span>
                    <span className="detail-value">{item.value || "-"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeMainTab === "Personal Info" && activeSubTab === "Analysis" && <ProfileAnalysisTab playerId={player.playerId} />}
          {activeMainTab === "Personal Info" && activeSubTab === "Matches" && <ProfileMatchesTab playerId={player.playerId} />}
          {activeMainTab === "Personal Info" && activeSubTab === "Teams" && <ProfileTeamsTab playerId={player.playerId} />}

          {activeMainTab === "Roles & Capabilities" && (
            <div className="roles-capabilities-section">
              <div className="roles-header">
                <p>
                  Manage your certified roles across the CricCenter ecosystem
                </p>
                {isEditingRoles ? (
                  <div className="roles-header-actions">
                    <button
                      className="cancel-roles-btn"
                      type="button"
                      onClick={handleCancelEditRoles}
                      disabled={isSavingRoles}
                    >
                      <RiCloseLine />
                      <span>Cancel</span>
                    </button>
                    <button
                      className="save-roles-btn"
                      type="button"
                      onClick={handleSaveRoles}
                      disabled={isSavingRoles}
                    >
                      <RiCheckLine />
                      <span>{isSavingRoles ? "Saving..." : "Save"}</span>
                    </button>
                  </div>
                ) : (
                  <button
                    className="edit-roles-btn"
                    type="button"
                    onClick={handleEditRoles}
                  >
                    <RiEditLine />
                    <span>Edit</span>
                  </button>
                )}
              </div>
              <div className="roles-list">
                {roles.map((role) => (
                  <div
                    className={`role-card role-${role.key} ${role.enabled ? "enabled" : "disabled"}${isEditingRoles && role.enabled ? " editing" : ""}`}
                    key={role.key}
                  >
                    <div className="role-card-main">
                      <div className={`role-icon ${role.key}`}>{role.icon}</div>
                      <span className="role-label">{role.label}</span>
                      <div className="role-card-actions">
                        {isEditingRoles && (
                          <button
                            className={`role-toggle ${role.enabled ? "on" : "off"}`}
                            type="button"
                            onClick={() => handleToggleRole(role.key)}
                            disabled={role.alwaysOn}
                          >
                            <div className="toggle-thumb" />
                          </button>
                        )}
                        {(role.enabled || isEditingRoles) && (
                          <button
                            className="role-expand-btn"
                            type="button"
                            onClick={() =>
                              setExpandedRole(
                                expandedRole === role.key ? null : role.key,
                              )
                            }
                          >
                            {expandedRole === role.key ? (
                              <LuChevronUp />
                            ) : (
                              <LuChevronDown />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    {expandedRole === role.key && (
                      <div className="role-card-expanded">
                        {role.key === "umpire" && (
                          <div className="umpire-details-grid">
                            <div className="umpire-field umpire-field-full">
                              <label>Umpire Location Availability</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="location-field-wrapper">
                                  <div className="location-capsules">
                                    {umpireFields.locations.map((loc) => (
                                      <span
                                        className="location-capsule"
                                        key={loc}
                                      >
                                        {loc}
                                        <button
                                          type="button"
                                          className="capsule-remove-btn"
                                          onClick={() =>
                                            setUmpireFields((prev) => ({
                                              ...prev,
                                              locations: prev.locations.filter(
                                                (l) => l !== loc,
                                              ),
                                            }))
                                          }
                                        >
                                          <RiCloseLine />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                  <div className="location-add-row">
                                    <input
                                      className="field-input"
                                      type="text"
                                      value={umpireFields.locationInput}
                                      onChange={(e) =>
                                        setUmpireFields((prev) => ({
                                          ...prev,
                                          locationInput: e.target.value,
                                        }))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          e.preventDefault();
                                          const city =
                                            umpireFields.locationInput.trim();
                                          if (
                                            city &&
                                            !umpireFields.locations.includes(
                                              city,
                                            )
                                          ) {
                                            setUmpireFields((prev) => ({
                                              ...prev,
                                              locations: [
                                                ...prev.locations,
                                                city,
                                              ],
                                              locationInput: "",
                                            }));
                                          }
                                        }
                                      }}
                                      placeholder="Enter city name"
                                    />
                                    <button
                                      type="button"
                                      className="location-add-btn"
                                      onClick={() => {
                                        const city =
                                          umpireFields.locationInput.trim();
                                        if (
                                          city &&
                                          !umpireFields.locations.includes(city)
                                        ) {
                                          setUmpireFields((prev) => ({
                                            ...prev,
                                            locations: [
                                              ...prev.locations,
                                              city,
                                            ],
                                            locationInput: "",
                                          }));
                                        }
                                      }}
                                    >
                                      Add
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="field-value">
                                  {umpireFields.locations.length > 0 ? (
                                    <div className="location-capsules">
                                      {umpireFields.locations.map((loc) => (
                                        <span
                                          className="location-capsule"
                                          key={loc}
                                        >
                                          {loc}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    "-"
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="umpire-field">
                              <label>Experience (in years)</label>
                              {isEditingRoles && role.enabled ? (
                                <input
                                  className="field-input"
                                  type="number"
                                  min={0}
                                  value={umpireFields.experience}
                                  onChange={(e) =>
                                    setUmpireFields((prev) => ({
                                      ...prev,
                                      experience: e.target.value,
                                    }))
                                  }
                                  placeholder="Enter experience"
                                />
                              ) : (
                                <div className="field-value">
                                  {umpireFields.experience || "-"}
                                </div>
                              )}
                            </div>
                            <div className="umpire-field">
                              <label>Fees</label>
                              {isEditingRoles && role.enabled ? (
                                <input
                                  className="field-input"
                                  type="number"
                                  min={0}
                                  value={umpireFields.fees}
                                  onChange={(e) =>
                                    setUmpireFields((prev) => ({
                                      ...prev,
                                      fees: e.target.value,
                                    }))
                                  }
                                  placeholder="Enter fees"
                                />
                              ) : (
                                <div className="field-value">
                                  {umpireFields.fees || "-"}
                                </div>
                              )}
                            </div>
                            <div className="umpire-field">
                              <label>Payment Frequency</label>
                              {isEditingRoles && role.enabled ? (
                                <select
                                  className="field-input field-select"
                                  value={umpireFields.frequency}
                                  onChange={(e) =>
                                    setUmpireFields((prev) => ({
                                      ...prev,
                                      frequency: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="">Select frequency</option>
                                  <option value="HOUR">Hour</option>
                                  <option value="MATCH">MATCH</option>
                                </select>
                              ) : (
                                <div className="field-value field-value-select">
                                  {umpireFields.frequency || "-"}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {role.key === "scorer" && (
                          <div className="scorer-details-grid">
                            <div className="scorer-field">
                              <label>Years of Experience</label>
                              {isEditingRoles && role.enabled ? (
                                <input
                                  className="field-input"
                                  type="number"
                                  min={0}
                                  value={scorerFields.experienceYears}
                                  onChange={(e) =>
                                    setScorerFields((prev) => ({
                                      ...prev,
                                      experienceYears: e.target.value,
                                    }))
                                  }
                                  placeholder="Experience"
                                />
                              ) : (
                                <div className="field-value">
                                  {scorerFields.experienceYears || "-"}
                                </div>
                              )}
                            </div>
                            <div className="scorer-field">
                              <label>Scoring Interface Preference</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="multiselect-wrapper">
                                  <button
                                    type="button"
                                    className="multiselect-trigger field-input field-select"
                                    onClick={() => {
                                      if (
                                        openDropdown ===
                                        "scoringInterfacePreferred"
                                      ) {
                                        setOpenDropdown(null);
                                      } else {
                                        setTempSelections((prev) => ({
                                          ...prev,
                                          scoringInterfacePreferred: [
                                            ...scorerFields.scoringInterfacePreferred,
                                          ],
                                        }));
                                        setOpenDropdown(
                                          "scoringInterfacePreferred",
                                        );
                                      }
                                    }}
                                  >
                                    {scorerFields.scoringInterfacePreferred
                                      .length > 0
                                      ? scorerFields.scoringInterfacePreferred.join(
                                          ", ",
                                        )
                                      : "Select interface"}
                                  </button>
                                  {openDropdown ===
                                    "scoringInterfacePreferred" && (
                                    <div className="multiselect-dropdown">
                                      {["Mobile", "Tablet", "Desktop"].map(
                                        (opt) => (
                                          <label
                                            key={opt}
                                            className={`multiselect-option${tempSelections.scoringInterfacePreferred.includes(opt) ? " selected" : ""}`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={tempSelections.scoringInterfacePreferred.includes(
                                                opt,
                                              )}
                                              onChange={() =>
                                                setTempSelections((prev) => ({
                                                  ...prev,
                                                  scoringInterfacePreferred:
                                                    prev.scoringInterfacePreferred.includes(
                                                      opt,
                                                    )
                                                      ? prev.scoringInterfacePreferred.filter(
                                                          (v) => v !== opt,
                                                        )
                                                      : [
                                                          ...prev.scoringInterfacePreferred,
                                                          opt,
                                                        ],
                                                }))
                                              }
                                            />
                                            {opt}
                                          </label>
                                        ),
                                      )}
                                      <div className="multiselect-actions">
                                        <button
                                          type="button"
                                          className="multiselect-clear"
                                          onClick={() =>
                                            setTempSelections((prev) => ({
                                              ...prev,
                                              scoringInterfacePreferred: [],
                                            }))
                                          }
                                        >
                                          Clear All
                                        </button>
                                        <button
                                          type="button"
                                          className="multiselect-apply"
                                          onClick={() => {
                                            setScorerFields((prev) => ({
                                              ...prev,
                                              scoringInterfacePreferred:
                                                tempSelections.scoringInterfacePreferred,
                                            }));
                                            setOpenDropdown(null);
                                          }}
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="field-value">
                                  {scorerFields.scoringInterfacePreferred.join(
                                    ", ",
                                  ) || "-"}
                                </div>
                              )}
                            </div>
                            <div className="scorer-field">
                              <label>Preferred City</label>
                              {isEditingRoles && role.enabled ? (
                                <input
                                  className="field-input"
                                  type="text"
                                  value={scorerFields.city}
                                  onChange={(e) =>
                                    setScorerFields((prev) => ({
                                      ...prev,
                                      city: e.target.value,
                                    }))
                                  }
                                  placeholder="Enter City Name"
                                />
                              ) : (
                                <div className="field-value">
                                  {scorerFields.city || "-"}
                                </div>
                              )}
                            </div>
                            <div className="scorer-field">
                              <label>Familiar Scoring Method</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="multiselect-wrapper">
                                  <button
                                    type="button"
                                    className="multiselect-trigger field-input field-select"
                                    onClick={() => {
                                      if (
                                        openDropdown ===
                                        "familiarScoringMethods"
                                      ) {
                                        setOpenDropdown(null);
                                      } else {
                                        setTempSelections((prev) => ({
                                          ...prev,
                                          familiarScoringMethods: [
                                            ...scorerFields.familiarScoringMethods,
                                          ],
                                        }));
                                        setOpenDropdown(
                                          "familiarScoringMethods",
                                        );
                                      }
                                    }}
                                  >
                                    {scorerFields.familiarScoringMethods
                                      .length > 0
                                      ? scorerFields.familiarScoringMethods.join(
                                          ", ",
                                        )
                                      : "Select scoring method"}
                                  </button>
                                  {openDropdown ===
                                    "familiarScoringMethods" && (
                                    <div className="multiselect-dropdown">
                                      {["Linear", "Manual", "Digital"].map(
                                        (opt) => (
                                          <label
                                            key={opt}
                                            className={`multiselect-option${tempSelections.familiarScoringMethods.includes(opt) ? " selected" : ""}`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={tempSelections.familiarScoringMethods.includes(
                                                opt,
                                              )}
                                              onChange={() =>
                                                setTempSelections((prev) => ({
                                                  ...prev,
                                                  familiarScoringMethods:
                                                    prev.familiarScoringMethods.includes(
                                                      opt,
                                                    )
                                                      ? prev.familiarScoringMethods.filter(
                                                          (v) => v !== opt,
                                                        )
                                                      : [
                                                          ...prev.familiarScoringMethods,
                                                          opt,
                                                        ],
                                                }))
                                              }
                                            />
                                            {opt}
                                          </label>
                                        ),
                                      )}
                                      <div className="multiselect-actions">
                                        <button
                                          type="button"
                                          className="multiselect-clear"
                                          onClick={() =>
                                            setTempSelections((prev) => ({
                                              ...prev,
                                              familiarScoringMethods: [],
                                            }))
                                          }
                                        >
                                          Clear All
                                        </button>
                                        <button
                                          type="button"
                                          className="multiselect-apply"
                                          onClick={() => {
                                            setScorerFields((prev) => ({
                                              ...prev,
                                              familiarScoringMethods:
                                                tempSelections.familiarScoringMethods,
                                            }));
                                            setOpenDropdown(null);
                                          }}
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="field-value">
                                  {scorerFields.familiarScoringMethods.join(
                                    ", ",
                                  ) || "-"}
                                </div>
                              )}
                            </div>
                            {/* <div className="scorer-field">
                              <label>Certificate Upload</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="field-upload">
                                  <input
                                    className="field-input"
                                    type="text"
                                    value={scorerFields.certificateUrl}
                                    readOnly
                                    placeholder=""
                                  />
                                  <button className="upload-btn" type="button">
                                    Upload
                                  </button>
                                </div>
                              ) : (
                                <div className="field-value">
                                  {scorerFields.certificateUrl || "-"}
                                </div>
                              )}
                            </div> */}
                            <div className="scorer-field">
                              <label>Fees (Per Match)</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="field-with-prefix">
                                  <span className="field-prefix">&#8377;</span>
                                  <input
                                    className="field-input"
                                    type="number"
                                    min={0}
                                    value={scorerFields.feesPerMatch}
                                    onChange={(e) =>
                                      setScorerFields((prev) => ({
                                        ...prev,
                                        feesPerMatch: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              ) : (
                                <div className="field-value">
                                  {scorerFields.feesPerMatch
                                    ? `₹${scorerFields.feesPerMatch}`
                                    : "-"}
                                </div>
                              )}
                            </div>
                            <div className="scorer-field">
                              <label>Fees (Per Day)</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="field-with-prefix">
                                  <span className="field-prefix">&#8377;</span>
                                  <input
                                    className="field-input"
                                    type="number"
                                    min={0}
                                    value={scorerFields.feesPerDay}
                                    onChange={(e) =>
                                      setScorerFields((prev) => ({
                                        ...prev,
                                        feesPerDay: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              ) : (
                                <div className="field-value">
                                  {scorerFields.feesPerDay
                                    ? `₹${scorerFields.feesPerDay}`
                                    : "-"}
                                </div>
                              )}
                            </div>
                            <div className="scorer-field">
                              <label>Language Known</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="multiselect-wrapper">
                                  <button
                                    type="button"
                                    className="multiselect-trigger field-input field-select"
                                    onClick={() => {
                                      if (openDropdown === "languagesKnown") {
                                        setOpenDropdown(null);
                                      } else {
                                        setTempSelections((prev) => ({
                                          ...prev,
                                          languagesKnown: [
                                            ...scorerFields.languagesKnown,
                                          ],
                                        }));
                                        setOpenDropdown("languagesKnown");
                                      }
                                    }}
                                  >
                                    {scorerFields.languagesKnown.length > 0
                                      ? scorerFields.languagesKnown.join(", ")
                                      : "Select languages"}
                                  </button>
                                  {openDropdown === "languagesKnown" && (
                                    <div className="multiselect-dropdown">
                                      {[
                                        "English",
                                        "Hindi",
                                        "Tamil",
                                        "Telugu",
                                        "Kannada",
                                        "Bengali",
                                        "Marathi",
                                      ].map((opt) => (
                                        <label
                                          key={opt}
                                          className={`multiselect-option${tempSelections.languagesKnown.includes(opt) ? " selected" : ""}`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={tempSelections.languagesKnown.includes(
                                              opt,
                                            )}
                                            onChange={() =>
                                              setTempSelections((prev) => ({
                                                ...prev,
                                                languagesKnown:
                                                  prev.languagesKnown.includes(
                                                    opt,
                                                  )
                                                    ? prev.languagesKnown.filter(
                                                        (v) => v !== opt,
                                                      )
                                                    : [
                                                        ...prev.languagesKnown,
                                                        opt,
                                                      ],
                                              }))
                                            }
                                          />
                                          {opt}
                                        </label>
                                      ))}
                                      <div className="multiselect-actions">
                                        <button
                                          type="button"
                                          className="multiselect-clear"
                                          onClick={() =>
                                            setTempSelections((prev) => ({
                                              ...prev,
                                              languagesKnown: [],
                                            }))
                                          }
                                        >
                                          Clear All
                                        </button>
                                        <button
                                          type="button"
                                          className="multiselect-apply"
                                          onClick={() => {
                                            setScorerFields((prev) => ({
                                              ...prev,
                                              languagesKnown:
                                                tempSelections.languagesKnown,
                                            }));
                                            setOpenDropdown(null);
                                          }}
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="field-value">
                                  {scorerFields.languagesKnown.join(", ") ||
                                    "-"}
                                </div>
                              )}
                            </div>
                            <div className="scorer-field scorer-field-full">
                              <label>Bio</label>
                              {isEditingRoles && role.enabled ? (
                                <textarea
                                  className="field-input"
                                  value={scorerFields.bio}
                                  onChange={(e) =>
                                    setScorerFields((prev) => ({
                                      ...prev,
                                      bio: e.target.value,
                                    }))
                                  }
                                  placeholder="Enter bio"
                                />
                              ) : (
                                <div className="field-value">
                                  {scorerFields.bio || "-"}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {role.key === "streamer" && (
                          <div className="streamer-details-grid">
                            <div className="streamer-field">
                              <label>Camera Setup</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="multiselect-wrapper">
                                  <button
                                    type="button"
                                    className="multiselect-trigger field-input field-select"
                                    onClick={() => {
                                      if (openDropdown === "cameraSetup") {
                                        setOpenDropdown(null);
                                      } else {
                                        setTempSelections((prev) => ({
                                          ...prev,
                                          cameraSetup: [
                                            ...streamerFields.cameraSetup,
                                          ],
                                        }));
                                        setOpenDropdown("cameraSetup");
                                      }
                                    }}
                                  >
                                    {streamerFields.cameraSetup.length > 0
                                      ? streamerFields.cameraSetup.join(", ")
                                      : "Select camera setup"}
                                  </button>
                                  {openDropdown === "cameraSetup" && (
                                    <div className="multiselect-dropdown">
                                      {["Mobile", "DSLR", "Multi-Camera"].map(
                                        (opt) => (
                                          <label
                                            key={opt}
                                            className={`multiselect-option${tempSelections.cameraSetup.includes(opt) ? " selected" : ""}`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={tempSelections.cameraSetup.includes(
                                                opt,
                                              )}
                                              onChange={() =>
                                                setTempSelections((prev) => ({
                                                  ...prev,
                                                  cameraSetup:
                                                    prev.cameraSetup.includes(
                                                      opt,
                                                    )
                                                      ? prev.cameraSetup.filter(
                                                          (v) => v !== opt,
                                                        )
                                                      : [
                                                          ...prev.cameraSetup,
                                                          opt,
                                                        ],
                                                }))
                                              }
                                            />
                                            {opt}
                                          </label>
                                        ),
                                      )}
                                      <div className="multiselect-actions">
                                        <button
                                          type="button"
                                          className="multiselect-clear"
                                          onClick={() =>
                                            setTempSelections((prev) => ({
                                              ...prev,
                                              cameraSetup: [],
                                            }))
                                          }
                                        >
                                          Clear All
                                        </button>
                                        <button
                                          type="button"
                                          className="multiselect-apply"
                                          onClick={() => {
                                            setStreamerFields((prev) => ({
                                              ...prev,
                                              cameraSetup:
                                                tempSelections.cameraSetup,
                                            }));
                                            setOpenDropdown(null);
                                          }}
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="field-value">
                                  {streamerFields.cameraSetup.join(", ") || "-"}
                                </div>
                              )}
                            </div>
                            <div className="streamer-field">
                              <label>Streaming Device</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="multiselect-wrapper">
                                  <button
                                    type="button"
                                    className="multiselect-trigger field-input field-select"
                                    onClick={() => {
                                      if (openDropdown === "streamingDevice") {
                                        setOpenDropdown(null);
                                      } else {
                                        setTempSelections((prev) => ({
                                          ...prev,
                                          streamingDevice: [
                                            ...streamerFields.streamingDevice,
                                          ],
                                        }));
                                        setOpenDropdown("streamingDevice");
                                      }
                                    }}
                                  >
                                    {streamerFields.streamingDevice.length > 0
                                      ? streamerFields.streamingDevice.join(
                                          ", ",
                                        )
                                      : "Select streaming device"}
                                  </button>
                                  {openDropdown === "streamingDevice" && (
                                    <div className="multiselect-dropdown">
                                      {["Mobile", "Laptop", "PC"].map((opt) => (
                                        <label
                                          key={opt}
                                          className={`multiselect-option${tempSelections.streamingDevice.includes(opt) ? " selected" : ""}`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={tempSelections.streamingDevice.includes(
                                              opt,
                                            )}
                                            onChange={() =>
                                              setTempSelections((prev) => ({
                                                ...prev,
                                                streamingDevice:
                                                  prev.streamingDevice.includes(
                                                    opt,
                                                  )
                                                    ? prev.streamingDevice.filter(
                                                        (v) => v !== opt,
                                                      )
                                                    : [
                                                        ...prev.streamingDevice,
                                                        opt,
                                                      ],
                                              }))
                                            }
                                          />
                                          {opt}
                                        </label>
                                      ))}
                                      <div className="multiselect-actions">
                                        <button
                                          type="button"
                                          className="multiselect-clear"
                                          onClick={() =>
                                            setTempSelections((prev) => ({
                                              ...prev,
                                              streamingDevice: [],
                                            }))
                                          }
                                        >
                                          Clear All
                                        </button>
                                        <button
                                          type="button"
                                          className="multiselect-apply"
                                          onClick={() => {
                                            setStreamerFields((prev) => ({
                                              ...prev,
                                              streamingDevice:
                                                tempSelections.streamingDevice,
                                            }));
                                            setOpenDropdown(null);
                                          }}
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="field-value">
                                  {streamerFields.streamingDevice.join(", ") ||
                                    "-"}
                                </div>
                              )}
                            </div>
                            <div className="streamer-field">
                              <label>Audio Setup</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="multiselect-wrapper">
                                  <button
                                    type="button"
                                    className="multiselect-trigger field-input field-select"
                                    onClick={() => {
                                      if (openDropdown === "audioSetup") {
                                        setOpenDropdown(null);
                                      } else {
                                        setTempSelections((prev) => ({
                                          ...prev,
                                          audioSetup: [
                                            ...streamerFields.audioSetup,
                                          ],
                                        }));
                                        setOpenDropdown("audioSetup");
                                      }
                                    }}
                                  >
                                    {streamerFields.audioSetup.length > 0
                                      ? streamerFields.audioSetup.join(", ")
                                      : "Select audio setup"}
                                  </button>
                                  {openDropdown === "audioSetup" && (
                                    <div className="multiselect-dropdown">
                                      {[
                                        "No mic",
                                        "Basic mic",
                                        "Professional mic",
                                      ].map((opt) => (
                                        <label
                                          key={opt}
                                          className={`multiselect-option${tempSelections.audioSetup.includes(opt) ? " selected" : ""}`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={tempSelections.audioSetup.includes(
                                              opt,
                                            )}
                                            onChange={() =>
                                              setTempSelections((prev) => ({
                                                ...prev,
                                                audioSetup:
                                                  prev.audioSetup.includes(opt)
                                                    ? prev.audioSetup.filter(
                                                        (v) => v !== opt,
                                                      )
                                                    : [...prev.audioSetup, opt],
                                              }))
                                            }
                                          />
                                          {opt}
                                        </label>
                                      ))}
                                      <div className="multiselect-actions">
                                        <button
                                          type="button"
                                          className="multiselect-clear"
                                          onClick={() =>
                                            setTempSelections((prev) => ({
                                              ...prev,
                                              audioSetup: [],
                                            }))
                                          }
                                        >
                                          Clear All
                                        </button>
                                        <button
                                          type="button"
                                          className="multiselect-apply"
                                          onClick={() => {
                                            setStreamerFields((prev) => ({
                                              ...prev,
                                              audioSetup:
                                                tempSelections.audioSetup,
                                            }));
                                            setOpenDropdown(null);
                                          }}
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="field-value">
                                  {streamerFields.audioSetup.join(", ") || "-"}
                                </div>
                              )}
                            </div>
                            <div className="streamer-field">
                              <label>Streaming Platforms</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="multiselect-wrapper">
                                  <button
                                    type="button"
                                    className="multiselect-trigger field-input field-select"
                                    onClick={() => {
                                      if (
                                        openDropdown === "streamingPlatforms"
                                      ) {
                                        setOpenDropdown(null);
                                      } else {
                                        setTempSelections((prev) => ({
                                          ...prev,
                                          streamingPlatforms: [
                                            ...streamerFields.streamingPlatforms,
                                          ],
                                        }));
                                        setOpenDropdown("streamingPlatforms");
                                      }
                                    }}
                                  >
                                    {streamerFields.streamingPlatforms.length >
                                    0
                                      ? streamerFields.streamingPlatforms.join(
                                          ", ",
                                        )
                                      : "Select streaming platforms"}
                                  </button>
                                  {openDropdown === "streamingPlatforms" && (
                                    <div className="multiselect-dropdown">
                                      {["Youtube", "Facebook"].map((opt) => (
                                        <label
                                          key={opt}
                                          className={`multiselect-option${tempSelections.streamingPlatforms.includes(opt) ? " selected" : ""}`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={tempSelections.streamingPlatforms.includes(
                                              opt,
                                            )}
                                            onChange={() =>
                                              setTempSelections((prev) => ({
                                                ...prev,
                                                streamingPlatforms:
                                                  prev.streamingPlatforms.includes(
                                                    opt,
                                                  )
                                                    ? prev.streamingPlatforms.filter(
                                                        (v) => v !== opt,
                                                      )
                                                    : [
                                                        ...prev.streamingPlatforms,
                                                        opt,
                                                      ],
                                              }))
                                            }
                                          />
                                          {opt}
                                        </label>
                                      ))}
                                      <div className="multiselect-actions">
                                        <button
                                          type="button"
                                          className="multiselect-clear"
                                          onClick={() =>
                                            setTempSelections((prev) => ({
                                              ...prev,
                                              streamingPlatforms: [],
                                            }))
                                          }
                                        >
                                          Clear All
                                        </button>
                                        <button
                                          type="button"
                                          className="multiselect-apply"
                                          onClick={() => {
                                            setStreamerFields((prev) => ({
                                              ...prev,
                                              streamingPlatforms:
                                                tempSelections.streamingPlatforms,
                                            }));
                                            setOpenDropdown(null);
                                          }}
                                        >
                                          Apply
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="field-value">
                                  {streamerFields.streamingPlatforms.join(
                                    ", ",
                                  ) || "-"}
                                </div>
                              )}
                            </div>
                            <div className="streamer-field">
                              <label>Pricing (Per Match)</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="field-with-prefix">
                                  <span className="field-prefix">&#8377;</span>
                                  <input
                                    className="field-input"
                                    type="number"
                                    min={0}
                                    value={streamerFields.pricingPerMatch}
                                    onChange={(e) =>
                                      setStreamerFields((prev) => ({
                                        ...prev,
                                        pricingPerMatch: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              ) : (
                                <div className="field-value">
                                  {streamerFields.pricingPerMatch
                                    ? `₹${streamerFields.pricingPerMatch}`
                                    : "-"}
                                </div>
                              )}
                            </div>
                            <div className="streamer-field">
                              <label>Pricing (Per Day)</label>
                              {isEditingRoles && role.enabled ? (
                                <div className="field-with-prefix">
                                  <span className="field-prefix">&#8377;</span>
                                  <input
                                    className="field-input"
                                    type="number"
                                    min={0}
                                    value={streamerFields.pricingPerDay}
                                    onChange={(e) =>
                                      setStreamerFields((prev) => ({
                                        ...prev,
                                        pricingPerDay: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              ) : (
                                <div className="field-value">
                                  {streamerFields.pricingPerDay
                                    ? `₹${streamerFields.pricingPerDay}`
                                    : "-"}
                                </div>
                              )}
                            </div>
                            <div className="streamer-field streamer-field-full">
                              <label>Available for Paid Streaming</label>
                              {isEditingRoles && role.enabled ? (
                                <button
                                  className={`role-toggle ${streamerFields.availableForPaidStreaming ? "on" : "off"}`}
                                  type="button"
                                  onClick={() =>
                                    setStreamerFields((prev) => ({
                                      ...prev,
                                      availableForPaidStreaming:
                                        !prev.availableForPaidStreaming,
                                    }))
                                  }
                                >
                                  <div className="toggle-thumb" />
                                </button>
                              ) : (
                                <div className="field-value">
                                  {streamerFields.availableForPaidStreaming
                                    ? "Yes"
                                    : "No"}
                                </div>
                              )}
                            </div>
                            <div className="streamer-field streamer-field-full">
                              <label>Sample Stream Link</label>
                              {isEditingRoles && role.enabled ? (
                                <input
                                  className="field-input"
                                  type="url"
                                  value={streamerFields.sampleStreamLink}
                                  onChange={(e) =>
                                    setStreamerFields((prev) => ({
                                      ...prev,
                                      sampleStreamLink: e.target.value,
                                    }))
                                  }
                                  placeholder="Enter stream URL"
                                />
                              ) : (
                                <div className="field-value">
                                  {streamerFields.sampleStreamLink || "-"}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
